import React, {
  forwardRef,
  memo,
  startTransition,
  useEffect,
  useRef,
  useState
} from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import { i18n } from "base/i18n";
import { isEmpty } from "base/utils";

import { ICON_TABS_LIST, ICON_TABS_MAPPING } from "../../const.js";
import { SEARCH_SIZES, SearchInput } from "@widgets/inputs/search/index.jsx";
import { EMPTY_STATE_SIZES, EmptyState } from "@widgets/layouts/index.jsx";
import {
  KF_ILLUSTRATIONS,
  KfIllustration
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { ColorPicker } from "./color/index.jsx";
import { DEFAULT_ICON_COLOR } from "../../colors.js";
import { SvgFromUrl } from "@widgets/common/components/primitives/svg_from_url/index.jsx";

import styles from "./styles.css";

export function ShapePicker({
  selectedIcon,
  selectedColor,
  showColorPicker,
  onSelectIcon,
  iconPath,
  colorMapping,
  onSelectColor
}) {
  const [iconsObj, setIconsObj] = useState(ICON_TABS_MAPPING);

  function onSearch(searchStr) {
    if (!searchStr) {
      startTransition(() => setIconsObj(ICON_TABS_MAPPING));
    } else {
      startTransition(() => {
        let iconsObj = {};
        for (let key of ICON_TABS_LIST) {
          let icons = ICON_TABS_MAPPING[key].icons.filter((icon) => {
            return icon.searchName
              .toLowerCase()
              .includes(searchStr.toLowerCase());
          });
          if (icons.length) iconsObj[key] = { icons };
        }
        setIconsObj(iconsObj);
      });
    }
  }

  return (
    <div className={styles.shapeContainer}>
      <div className={styles.searchContainer}>
        <SearchInput
          placeholder={t`Search by name or category`}
          className={styles.search}
          onChangeDebounced={onSearch}
          debounceTime={300}
          onClear={() => onSearch("")}
          showColorPicker={showColorPicker}
          size={SEARCH_SIZES.SMALL}
        />
        {showColorPicker && (
          <ColorPicker
            selectedColor={selectedColor}
            colorMapping={colorMapping}
            onSelectColor={onSelectColor}
          />
        )}
      </div>
      <IconGroup
        iconsObj={iconsObj}
        selectedIconId={selectedIcon}
        selectedColor={selectedColor}
        onSelectIcon={onSelectIcon}
        iconPath={iconPath}
        colorMapping={colorMapping}
      />
    </div>
  );
}

ShapePicker.propTypes = {
  selectedIcon: PropTypes.string,
  selectedColor: PropTypes.string,
  onSelectIcon: PropTypes.func,
  showColorPicker: PropTypes.bool,
  iconPath: PropTypes.string,
  colorMapping: PropTypes.shape({
    color: PropTypes.string
  }),
  onSelectColor: PropTypes.func
};

function IconGroup({
  iconsObj,
  selectedIconId,
  selectedColor,
  onSelectIcon,
  iconPath,
  colorMapping
}) {
  const groupParenContainerRef = useRef();

  const [loadedIconsByCategory, setLoadedIconsByCategory] = useState([]);

  return (
    <div className={styles.iconGroupContainer} ref={groupParenContainerRef}>
      {!isEmpty(iconsObj) ? (
        <>
          {ICON_TABS_LIST.map((category) => {
            let icons = iconsObj[category]?.icons || [];
            return (
              icons.length !== 0 && (
                <div key={category} id={category}>
                  <>
                    <IconGroupTitle
                      id={ICON_TABS_MAPPING[category].id}
                      title={ICON_TABS_MAPPING[category].title}
                      loadedIconsByCategory={loadedIconsByCategory}
                      setLoadedIconsByCategory={setLoadedIconsByCategory}
                      ref={groupParenContainerRef}
                    />
                    <div className={`${styles.iconsContainer}`}>
                      {icons.map((value, index) => {
                        return (
                          <Icon
                            key={index}
                            selectedIconId={selectedIconId}
                            selectedColor={selectedColor}
                            onSelectIcon={onSelectIcon}
                            iconId={value.id}
                            iconPath={iconPath}
                            colorMapping={colorMapping}
                            loadIcon={loadedIconsByCategory.includes(
                              ICON_TABS_MAPPING[category].id
                            )}
                          />
                        );
                      })}
                    </div>
                  </>
                </div>
              )
            );
          })}
        </>
      ) : (
        <div className={styles.emptyState}>
          <EmptyState
            img={<KfIllustration icon={KF_ILLUSTRATIONS.UNHAPPY_FACE} />}
            text={t`No matching results found`}
            size={EMPTY_STATE_SIZES.SMALL}
          />
        </div>
      )}
    </div>
  );
}

IconGroup.propTypes = {
  iconsObj: PropTypes.shape({
    iconGroupTitle: PropTypes.shape({
      // iconGroupTitle - actual icon group title
      title: PropTypes.string,
      id: PropTypes.string,
      icons: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          names: PropTypes.arrayOf(PropTypes.string),
          searchName: PropTypes.string
        })
      )
    })
  }),
  selectedIconId: PropTypes.string,
  selectedColor: PropTypes.string,
  onSelectIcon: PropTypes.func,
  iconPath: PropTypes.string,
  colorMapping: PropTypes.shape({
    color: PropTypes.string
  })
};

const IconGroupTitle = forwardRef(
  ({ title, id, loadedIconsByCategory, setLoadedIconsByCategory }, ref) => {
    const targetForIntersection = useRef();

    useEffect(function loadOnScreenIconGroup() {
      const targetElement = targetForIntersection.current;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            loadedIconsByCategory.push(id);
            setLoadedIconsByCategory([...loadedIconsByCategory]);
            observer.unobserve(targetElement);
          }
        },
        {
          root: ref.current,
          threshold: 0.1
        }
      );
      targetElement && observer.observe(targetElement);
      return () => {
        observer.unobserve(targetElement);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div
        className={`${styles.iconCatergoryTitle} micro`}
        ref={targetForIntersection}
      >
        {i18n._(title)}
      </div>
    );
  }
);

IconGroupTitle.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  loadedIconsByCategory: PropTypes.arrayOf(PropTypes.string),
  setLoadedIconsByCategory: PropTypes.func
};

function IconComponent({
  selectedIconId,
  selectedColor,
  onSelectIcon,
  iconId,
  iconPath,
  colorMapping,
  loadIcon = false
}) {
  const [isIconError, setIsIconError] = useState(false);

  let iconStyle = "";
  let isSelectedIcon = selectedIconId === iconId;
  let iconColor = selectedColor ? selectedColor : DEFAULT_ICON_COLOR;

  if (isIconError && isSelectedIcon) {
    iconStyle = `${styles[iconColor]} ${styles.selected} ${styles.fallback}`;
  } else if (isIconError && !isSelectedIcon) {
    iconStyle = `${styles.fallback}`;
  } else if (isSelectedIcon) {
    iconStyle = `${styles[iconColor]} ${styles.selected} `;
  } else {
    iconStyle = `${styles[iconColor]}`;
  }

  return (
    <div
      className={`${styles.iconsPluginContainer} ${iconStyle}`}
      id={iconId}
      style={{
        "--icon-color": selectedColor
          ? `var(${colorMapping[selectedColor]})`
          : `var(${colorMapping[DEFAULT_ICON_COLOR]})`
      }}
      onClick={!isIconError ? () => onSelectIcon(iconId) : null}
    >
      {loadIcon && (
        <SvgFromUrl
          url={`${iconPath}/${iconId}/${iconId}.svg`}
          checkIsIconFailedtoFetch={setIsIconError}
          iconColor={
            selectedColor
              ? colorMapping[selectedColor]
              : colorMapping[DEFAULT_ICON_COLOR]
          }
          fallBackIconColor={"--kf-color-gray-300"}
        />
      )}
    </div>
  );
}

const Icon = memo(IconComponent);

IconComponent.propTypes = {
  selectedIconId: PropTypes.string,
  selectedColor: PropTypes.string,
  onSelectIcon: PropTypes.func,
  iconId: PropTypes.string,
  iconPath: PropTypes.string,
  colorMapping: PropTypes.shape({
    color: PropTypes.string
  }),
  loadIcon: PropTypes.bool
};
